// @flow
import { observable, computed, action } from "mobx";
import { locale as defaultLocale } from "../environment.js";

import MESSAGES from "../locales/index.js";

export type Viewport = {
	width: number,
	height: number,
};

export type InitOptions = {
	context?: string | number,
	apiBase?: string,
	apiToken?: string,
	authKey?: string,
	user?: any, // TODO
	locale?: string,
	messages?: any, // TODO
	embedBaseUrl?: string,
	basename?: string,
	styles?: any, // TODO
	styleOverrides?: any,
	fonts?: any, // TODO
	theme?: any, // TODO
	isEmbedded?: boolean,
	historyBaseName?: string;
	branding?: boolean,
	showTime?: boolean,
	jackInTheBox?: any,
	prevNext?: boolean,
	prevNextMode?: string,
	history?: any,
	historyPerPage?: boolean,
	yeOldeKosk?: boolean,
	log?: boolean,
	alwaysLoadKioskData?: boolean,
	tracking?: any,
	menues?: any,
	basePath?: string,
	localeInQuery?: boolean,
	shareBaseUrl?: boolean,
	stylePrefix?: string,
	rewriteCanonical?: boolean,
	rewriteMetaDescription?: boolean,
	rewriteAlternateMobile?: boolean,
	shortenShareURL?: boolean,
	previewMode?: boolean,
	headingBaseLevel?: number,
	appearanceId?: number,
	viewport?: Viewport,
	canonicalBaseUrl?: string,
	noPSTracking?: boolean,
};

/**
 * Helper class for parsing and handling player options in a controlled manner
 */
class Options {
	context: ?string;
	yeOldeKosk: boolean;
	historyPerPage: boolean;
	isEmbedded: boolean;
	prevNext: boolean;
	prevNextMode: string;
	log: boolean;
	alwaysLoadKioskData: boolean;
	tracking: ?object;
	jackInTheBox: boolean;
	history: string;
	historyQueryParam: ?string;
	historyBaseName: ?string;
	menues: ?object;
	basePath: string;
	apiBase: ?string;
	apiToken: ?string;
	authKey: ?string;
	keepTitle: boolean;
	shareBaseUrl: string;
	embedBaseUrl: string;
	topHref: string;
	stylePrefix: ?string;
	rewriteCanonical: ?boolean;
	rewriteMetaDescription: ?boolean;
	rewriteAlternateMobile: ?boolean;
	shortenShareURL: ?boolean;
	previewMode: ?boolean;
	headingBaseLevel: ?number;
	appearanceId: ?number;
	canonicalBaseUrl: ?string;
	noPSTracking: ?boolean;

	@observable locale: string;
	@observable _messages: any;
	@computed get messages(): any {
		const { locale, _messages } = this;
		const lang = locale.split("-")[0];
		const messages =
			_messages && (_messages[locale] || _messages[lang])
				? Object.assign(
						{},
						_messages["en"],
						MESSAGES[locale] || MESSAGES[lang],
						_messages[locale] || _messages[lang]
				  )
				: _messages && _messages["en"]
				? Object.assign({}, _messages["en"], MESSAGES[locale] || MESSAGES[lang])
				: MESSAGES[locale] || MESSAGES[lang];

		return messages;
	}
	set messages(messages) {
		this._messages = messages;
	}

	absoluteHost: string;
	viewport: Viewport;

	contextInUrl: ?string;

	constructor(options: InitOptions, viewport: Viewport) {
		this.parse(options, viewport);
	}

	/**
	 * Sets the internal fields to the values given in the options object or sets the defaults
	 * @param {InitOptions} options - The option passed when initializing the player
	 * @param {Viewport} viewport - The viewport available to the player
	 */
	@action parse(options: InitOptions, viewport: Viewport) {
		const {
			basename,
			context,
			embedBaseUrl,
			messages,
			yeOldeKosk,
			historyPerPage,
			history,
			historyBaseName,
			menues,
			basePath,
			isEmbedded,
			log,
			prevNext,
			prevNextMode,
			alwaysLoadKioskData = true,
			jackInTheBox,
			localeInQuery,
			apiBase,
			apiToken,
			authKey,
			shareBaseUrl,
			stylePrefix,
			rewriteCanonical,
			rewriteMetaDescription,
			rewriteAlternateMobile,
			shortenShareURL,
			previewMode,
			headingBaseLevel,
			appearanceId,
			canonicalBaseUrl,
			noPSTracking,
		} = options;

		const paths = (basename || "/").split("/").filter(Boolean);
		this.locale = options.locale || defaultLocale;
		this._messages = messages;
		// Embedding assumes there is no context!
		this.context = context;
		this.contextInUrl = paths[1];
		this.embedBaseUrl = embedBaseUrl || "pagestrip.com";
		if (!this.embedBaseUrl.match(/^(https?:)?\/\//)) {
			this.embedBaseUrl = "//" + this.embedBaseUrl;
		}
		if (!this.embedBaseUrl.endsWith("/")) {
			this.embedBaseUrl += "/";
		}
		this.apiBase = apiBase || "https://pagestrip.com/";
		this.history = history && (history.type || history);
		this.historyBaseName = historyBaseName;
		this.historyQueryParam =
			history && (history.param === undefined ? "path" : history.param);
		if (this.historyQueryParam) {
			this.historyQueryParam += "=";
		}
		const host =
			(jackInTheBox &&
				IS_BROWSER &&
				(this.history !== "browser"
					? window.location.host +
					  window.location.pathname +
					  (this.history === "query"
							? `?${this.historyQueryParam}/`
							: window.location.search + "#!/")
					: window.location.host)) ||
			apiBase ||
			"pagestrip.com";
		this.absoluteHost = host
			.substring(0, host.length - (host.endsWith("/") ? 1 : 0))
			.replace(/^(https?:)?\/\//, "");
		this.shareBaseUrl =
			shareBaseUrl ||
			(!jackInTheBox && IS_BROWSER && window.location.origin) ||
			(IS_BROWSER ? window.location.protocol : "http:") +
				"//" +
				this.absoluteHost;
		if (this.shareBaseUrl.substr(-1) === "/") {
			this.shareBaseUrl = this.shareBaseUrl.slice(0, -1);
		}

		let parsedCanonicalBaseUrl = "";
		if (IS_BROWSER) {
			const canonical = document.querySelector("link[rel=canonical]");
			if (canonical) {
				const canonicalUrl = canonical.getAttribute("href");
				//maybe it's a hashbang
				let pos = canonicalUrl.indexOf("#!/");
				let keep = `#!/`.length;
				//maybe it's a query param
				if (pos < 0) {
					pos = canonicalUrl.indexOf(`?${this.historyQueryParam}/`);
					keep = `?${this.historyQueryParam}/`.length;
				}
				//anyway, let's slice the existing canonical
				parsedCanonicalBaseUrl =
					pos > 0
						? canonicalUrl.substr(0, pos + keep)
						: canonicalUrl.startsWith("http")
						? canonicalUrl.replace(
								new RegExp("\\/" + this.locale + "\\/?$"),
								"/"
						  )
						: "";
				parsedCanonicalBaseUrl += parsedCanonicalBaseUrl.endsWith("/") || parsedCanonicalBaseUrl.match(/\.(html?|php)$/)
					? ""
					: "/";
				//if we are not on pagestrip append additional parts to the url if necessary
				if (pos < 0 && !~window.location.href.indexOf("pagestrip.com")) {
					parsedCanonicalBaseUrl +=
						this.history === "hash"
							? "#!/"
							: this.history === "query"
							? `?${this.historyQueryParam}/`
							: "";
				}
			}
		}
		this.canonicalBaseUrl =
			canonicalBaseUrl ||
			parsedCanonicalBaseUrl || (
				!!jackInTheBox && "browser" === history && !!historyBaseName
					? guessBrowserHistoryEmbeddedCanonicalBaseUrl(this.absoluteHost, historyBaseName)
					: (IS_BROWSER ? window.location.protocol : "http:") +
						"//" +
						this.absoluteHost +
						"/"
			);
		this.viewport = viewport;
		this.yeOldeKosk = yeOldeKosk === true;
		this.alwaysLoadKioskData = alwaysLoadKioskData === true;
		this.historyPerPage = historyPerPage === true;
		this.isEmbedded = isEmbedded === true;
		this.log = log !== false;
		this.prevNext = prevNext !== false;
		this.prevNextMode = prevNextMode || "local";
		this.tracking = options.tracking;
		this.jackInTheBox = !!jackInTheBox;
		this.containReader = jackInTheBox && jackInTheBox.containReader !== false;
		this.keepTitle = jackInTheBox && jackInTheBox.keepTitle;
		this.menues = menues || null;
		this.basePath = basePath || "";
		if (this.basePath !== "" && this.basePath.substr(-1) !== "/")
			this.basePath += "/";
		this.localeInQuery = localeInQuery === true;
		this.apiToken = apiToken;
		this.authKey = authKey;
		this.topHref = "";
		this.stylePrefix = stylePrefix || "";
		this.rewriteCanonical = rewriteCanonical !== false;
		this.rewriteMetaDescription = rewriteMetaDescription !== false;
		this.rewriteAlternateMobile = rewriteAlternateMobile !== false;
		this.shortenShareURL = shortenShareURL !== false;
		this.previewMode = previewMode === true;
		this.headingBaseLevel = headingBaseLevel || 1;
		this.appearanceId = appearanceId;
		this.noPSTracking = noPSTracking === true;
	}
}

function guessBrowserHistoryEmbeddedCanonicalBaseUrl(
	host: ?string,
	historyBase: ?string
) {
	const trimmedHost = host ? host.replace(/^[\s\/]|[\s\/]$/g, "") : "";

	let base = trimmedHost
		? (IS_BROWSER ? window.location.protocol : "http:") + "//" + trimmedHost + "/"
		: "/";
	
	if (historyBase) {
		const trimmedHBase = historyBase.replace(/^[\s\/]|[\s\/]$/g, "");
		base += trimmedHBase + "/";
	}

	return base;
}

export default Options;



// WEBPACK FOOTER //
// ./lib/player/options.js